import React, { useState, useEffect } from 'react';
import {
    Button, TextField, Box, CssBaseline, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, Grid, Typography, Paper,
    createTheme, ThemeProvider, Avatar, Snackbar, InputAdornment
} from '@mui/material';
import { styled } from '@mui/system';
import OrderSummary from '../components/OrderSummary';
import User from '../data/User';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';

const defaultTheme = createTheme({
    palette: {
        primary: {
            main: '#e88ba9',
        },
        background: {
            default: '#fffafb',
        },
        text: {
            primary: '#5c434a',
            secondary: '#8c7077',
        },
    },
    typography: {
        fontFamily: '"Outfit", "Inter", sans-serif',
        h4: {
            fontFamily: '"Playfair Display", serif',
            color: '#d46a89',
            fontWeight: 700
        },
        h5: {
            fontFamily: '"Playfair Display", serif',
            color: '#d46a89',
            fontWeight: 600
        }
    },
});

const BakeryButton = styled(Button)({
    background: '#e88ba9',
    borderRadius: '30px',
    border: 0,
    color: 'white',
    padding: '10px 30px',
    boxShadow: '0 8px 16px rgba(232, 139, 169, 0.4)',
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 12px 20px rgba(212, 106, 137, 0.6)',
        background: '#d46a89',
    },
    '&:active': {
        transform: 'translateY(0)',
    }
});

const OutlineButton = styled(Button)({
    borderRadius: '30px',
    color: '#d46a89',
    borderColor: '#e88ba9',
    padding: '9px 29px',
    fontWeight: 600,
    textTransform: 'none',
    border: '2px solid #e88ba9',
    transition: 'all 0.3s ease',
    '&:hover': {
        color: 'white',
        backgroundColor: '#e88ba9',
        borderColor: '#e88ba9',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 15px rgba(232, 139, 169, 0.4)',
    },
    '&:active': {
        transform: 'translateY(0)',
    }
});

const SoftTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#fff',
        borderRadius: '16px',
        transition: 'all 0.3s ease',
        '& fieldset': {
            borderColor: 'rgba(232, 139, 169, 0.3)',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(232, 139, 169, 0.6)',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#e88ba9',
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root': {
        color: '#8c7077',
        '&.Mui-focused': {
            color: '#e88ba9',
        }
    },
    '& .MuiSvgIcon-root': {
        color: '#d46a89',
    }
});

const UserProfile = () => {
    const [fullname, setfullname] = useState("");
    const [email, setemail] = useState("");
    const [address, setaddress] = useState("");
    const [password, setpassword] = useState("");
    const [oldPassword, setoldpassword] = useState("");
    const [confirmPassword, setconfirmpassword] = useState("");
    const [phone, setphone] = useState("");
    const [profilePic, setProfilePic] = useState("");
    const [open, setOpen] = useState(false);
    const [alertMsg, setAlertMsg] = useState({ text: '', type: 'success' });

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setpassword("");
        setconfirmpassword("");
        setoldpassword("");
    };

    const handleChangePassword = async () => {
        if (!password || !confirmPassword || !oldPassword) {
            setAlertMsg({ text: "All fields are required.", type: 'error' });
            return;
        }
        if (password === confirmPassword) {
            let result = await User.instance.changePassword(oldPassword, password);
            if (result.success) {
                handleClose();
                setAlertMsg({ text: result.message || "Password changed successfully!", type: 'success' });
            } else {
                setAlertMsg({ text: result.message || "Password change failed.", type: 'error' });
            }
        } else {
            setAlertMsg({ text: "New passwords do not match!", type: 'error' });
        }
    };

    const handleSubmit = async () => {
        let formdata = {
            name: fullname,
            email: email,
            phone: phone,
            address: address,
            profilePic: profilePic
        };
        let res = await User.instance.updateData(formdata);
        if (res) {
            setAlertMsg({ text: "Profile updated successfully!", type: 'success' });
        } else {
            setAlertMsg({ text: "Profile update failed. Please check your inputs.", type: 'error' });
        }
    };

    const handleCloseAlert = (e, reason) => {
        if (reason === 'clickaway') return;
        setAlertMsg({ text: '', type: 'success' });
    };

    useEffect(() => {
        User.instance.fetchData().then(() => {
            setfullname(User.instance.name || '');
            setemail(User.instance.email || '');
            setphone(User.instance.phone || '');
            setaddress(User.instance.address || '');
            setProfilePic(User.instance.profilePic || '');
        });
    }, []);

    return (
        <ThemeProvider theme={defaultTheme}>
            <Box sx={{ background: '#fffafb', minHeight: '100vh', py: 6 }}>
                <CssBaseline />
                <Grid container spacing={4} maxWidth="1200px" mx="auto" px={3}>
                    {/* Profile Information Profile Card */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 4, borderRadius: '32px', border: '1px solid #fcf4f4', background: '#fff', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.03)', height: '100%' }}>
                            {profilePic ? (
                                <Box
                                    component="img"
                                    src={profilePic}
                                    alt="Profile avatar"
                                    sx={{
                                        width: 120, height: 120, mx: 'auto', mb: 3,
                                        borderRadius: '50%', objectFit: 'cover',
                                        border: '4px solid #fffafb', boxShadow: '0 8px 16px rgba(232, 139, 169, 0.2)'
                                    }}
                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                />
                            ) : null}
                            <Avatar
                                sx={{
                                    width: 120, height: 120, mx: 'auto', mb: 3,
                                    bgcolor: '#e88ba9', fontSize: '3rem', fontWeight: 600,
                                    border: '4px solid #fffafb', boxShadow: '0 8px 16px rgba(232, 139, 169, 0.2)',
                                    display: profilePic ? 'none' : 'flex'
                                }}
                            >
                                {fullname ? fullname.charAt(0).toUpperCase() : '👤'}
                            </Avatar>
                            <Typography variant="h4" gutterBottom>
                                {fullname || 'My Profile'}
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#8c7077', mb: 4 }}>
                                Sweeten up your account details here.
                            </Typography>

                            <Box component="form" noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <SoftTextField
                                    label="Full Name"
                                    value={fullname}
                                    onChange={(e) => setfullname(e.target.value)}
                                    fullWidth
                                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment> }}
                                />
                                <SoftTextField
                                    label="Email Address"
                                    value={email}
                                    onChange={(e) => setemail(e.target.value)}
                                    fullWidth
                                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment> }}
                                />
                                <SoftTextField
                                    label="Contact No"
                                    value={phone}
                                    onChange={(e) => setphone(e.target.value)}
                                    fullWidth
                                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment> }}
                                />
                                <SoftTextField
                                    label="Delivery Address"
                                    value={address}
                                    onChange={(e) => setaddress(e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><HomeIcon fontSize="small" /></InputAdornment> }}
                                />

                                <SoftTextField
                                    label="Profile Picture URL (Optional)"
                                    value={profilePic}
                                    onChange={(e) => setProfilePic(e.target.value)}
                                    fullWidth
                                    placeholder="https://example.com/my-photo.jpg"
                                />

                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <BakeryButton onClick={handleSubmit} fullWidth>
                                        Save Changes
                                    </BakeryButton>
                                    <OutlineButton onClick={handleOpen} fullWidth>
                                        Change Password
                                    </OutlineButton>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Orders Summary Display */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} sx={{ p: 4, borderRadius: '32px', border: '1px solid #fcf4f4', background: '#fff', boxShadow: '0 20px 40px rgba(0,0,0,0.03)', height: '100%' }}>
                            <OrderSummary />
                        </Paper>
                    </Grid>
                </Grid>

                {/* Password Dialog */}
                <Dialog
                    open={open}
                    onClose={handleClose}
                    PaperProps={{
                        sx: {
                            borderRadius: '24px',
                            p: 2,
                            boxShadow: '0 20px 40px rgba(232, 139, 169, 0.2)'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 700, fontSize: '1.5rem', textAlign: 'center' }}>
                        Update Security Key
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: '#8c7077', mb: 3, textAlign: 'center' }}>
                            Please enter your current password, and your new desired password to update it.
                        </DialogContentText>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                            <SoftTextField
                                label="Old Password"
                                type="password"
                                value={oldPassword}
                                onChange={(e) => setoldpassword(e.target.value)}
                                fullWidth
                            />
                            <SoftTextField
                                label="New Password"
                                type="password"
                                value={password}
                                onChange={(e) => setpassword(e.target.value)}
                                fullWidth
                            />
                            <SoftTextField
                                label="Confirm New Password"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setconfirmpassword(e.target.value)}
                                fullWidth
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 1, gap: 2 }}>
                        <Button onClick={handleClose} sx={{ color: '#8c7077', fontWeight: 600, textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <BakeryButton onClick={handleChangePassword} sx={{ m: 0 }}>
                            Set New Password
                        </BakeryButton>
                    </DialogActions>
                </Dialog>

                {/* SnackBar for Alerts */}
                <Snackbar
                    message={alertMsg.text}
                    autoHideDuration={4000}
                    open={alertMsg.text !== ''}
                    onClose={handleCloseAlert}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    ContentProps={{
                        sx: {
                            background: '#fff',
                            color: alertMsg.type === 'error' ? '#e53935' : '#d46a89',
                            fontWeight: 'bold',
                            border: `1px solid ${alertMsg.type === 'error' ? '#ef9a9a' : '#f4a2bc'}`,
                            boxShadow: `0 10px 30px ${alertMsg.type === 'error' ? 'rgba(229, 57, 53, 0.2)' : 'rgba(212, 106, 137, 0.3)'}`
                        }
                    }}
                />
            </Box>
        </ThemeProvider>
    );
}

export default UserProfile;